import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { AxiosProgressEvent } from 'axios'

import { api } from '../lib/api'
// URL import désactivé : YouTube/Vimeo bloquent les IPs datacenter
// import { parseVideoUrl, getProviderIcon, getProviderName, type VideoMetadata } from '../lib/videoUrlUtils'

type Subject = { id: number; title: string; source: string }
type Course = { id: string; title: string }

type ContentUploadPanelProps = {
  open: boolean
  onClose: () => void
  onUploaded?: () => void
}

export default function ContentUploadPanel({ open, onClose, onUploaded }: ContentUploadPanelProps) {
  const [subjects, setSubjects] = useState<Subject[]>([])
  const [courses, setCourses] = useState<Course[]>([])
  const [subjectId, setSubjectId] = useState<number | ''>('')
  const [courseId, setCourseId] = useState('')
  const [title, setTitle] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [dragOver, setDragOver] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)
  const [progress, setProgress] = useState(0)
  const inputRef = useRef<HTMLInputElement | null>(null)

  const resetForm = useCallback(() => {
    setSubjectId('')
    setCourseId('')
    setTitle('')
    setFile(null)
    setDragOver(false)
    setError(null)
    setSuccess(null)
    setProgress(0)
  }, [])

  const handleClose = useCallback(() => {
    resetForm()
    onClose()
  }, [onClose, resetForm])

  useEffect(() => {
    if (!open) {
      return
    }

    let cancelled = false

    const load = async () => {
      try {
        const { data } = await api.get('/subjects')
        if (!cancelled) {
          setSubjects(data || [])
        }
      } catch (e: any) {
        if (!cancelled) {
          setError(e?.response?.data?.detail || 'Erreur chargement des sujets')
        }
      }

      try {
        const { data } = await api.get('/wp/courses')
        if (!cancelled) {
          setCourses(data || [])
        }
      } catch {
        if (!cancelled) {
          setCourses([])
        }
      }
    }

    load()

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleClose()
      }
    }

    document.addEventListener('keydown', onKeyDown)
    return () => {
      cancelled = true
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [handleClose, open])

  useEffect(() => {
    if (!open) {
      resetForm()
    }
  }, [open, resetForm])

  const dropHandlers = useMemo(
    () => ({
      onDragOver: (event: React.DragEvent) => {
        event.preventDefault()
        setDragOver(true)
      },
      onDragLeave: () => setDragOver(false),
      onDrop: (event: React.DragEvent) => {
        event.preventDefault()
        setDragOver(false)
        const dropped = event.dataTransfer.files?.[0]
        if (dropped) {
          setFile(dropped)
          setError(null)
          setSuccess(null)
        }
      },
    }), [],
  )

  const handleSubmit = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault()
      if (!file) {
        setError('Choisir un fichier')
        return
      }

      setError(null)
      setSuccess(null)
      setProgress(0)

      const form = new FormData()
      form.append('file', file)
      if (title.trim()) form.append('title', title.trim())
      if (subjectId) form.append('subject_id', String(subjectId))
      else if (courseId.trim()) form.append('course_id', courseId.trim())

      try {
        setUploading(true)
        const { data } = await api.post('/upload', form, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (evt: AxiosProgressEvent) => {
            const loaded = evt.loaded || 0
            const total = evt.total || 0
            if (total > 0) {
              setProgress(Math.round((loaded / total) * 100))
            }
          },
        })
        if (data.status === 'processing') {
          setSuccess(`Upload terminé • Traitement en cours... (ID: ${data.media_id})`)
        } else {
          setSuccess(`Upload terminé • media_id=${data.media_id} • chunks=${data.chunks || 0}`)
        }
        setFile(null)
        setTitle('')
        setCourseId('')
        setProgress(100)
        onUploaded?.()
      } catch (e: any) {
        setError(e?.response?.data?.detail || e?.message || 'Erreur upload')
      } finally {
        setUploading(false)
      }
    },
    [courseId, file, onUploaded, subjectId, title],
  )

  if (!open) {
    return null
  }

  return (
    <div className="fixed inset-0 z-[120]">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={handleClose} aria-hidden />
      <aside
        className="absolute inset-y-0 right-0 flex w-full max-w-xl flex-col overflow-hidden border-l border-emerald-100/70 bg-white/95 shadow-2xl shadow-emerald-500/30 transition-transform duration-300 ease-out dark:border-emerald-900/60 dark:bg-zinc-950/95"
        role="dialog"
        aria-modal="true"
      >
        <header className="flex items-start justify-between border-b border-emerald-100/70 px-6 py-5 dark:border-emerald-900/60">
          <div>
            <h2 className="text-lg font-semibold text-zinc-900 dark:text-white">Ajouter un contenu</h2>
            <p className="text-sm text-zinc-500 dark:text-zinc-400">
              Importez un nouveau média : il sera automatiquement transcrit puis indexé.
            </p>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="rounded-full border border-zinc-200/70 px-3 py-1 text-sm font-semibold text-zinc-600 transition hover:bg-zinc-100/70 dark:border-zinc-700 dark:text-zinc-200 dark:hover:bg-zinc-800/60"
          >
            Fermer
          </button>
        </header>

        <form className="flex-1 overflow-y-auto px-6 py-5" onSubmit={handleSubmit}>
          <div className="space-y-6">
            <div className="grid gap-5 sm:grid-cols-2">
              <div className="space-y-2">
                <label className="text-xs font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400" htmlFor="upload-subject">
                  Sujet
                </label>
                <select
                  id="upload-subject"
                  value={subjectId}
                  onChange={(event) => {
                    const value = event.target.value
                    setSubjectId(value ? Number(value) : '')
                    if (value) {
                      setCourseId('')
                    }
                  }}
                  disabled={uploading}
                  className="w-full rounded-xl border border-zinc-200/70 bg-white/90 px-3 py-2 text-sm text-zinc-700 shadow-sm transition focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-200 dark:border-zinc-800 dark:bg-zinc-900/80 dark:text-zinc-200 dark:focus:border-emerald-500 dark:focus:ring-emerald-900/60"
                  data-onboarding="subject-select"
                >
                  <option value="">— choisir —</option>
                  {subjects.map((subject) => (
                    <option key={subject.id} value={subject.id}>
                      {subject.title} {subject.source === 'wordpress' ? '(WP)' : ''}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-zinc-500 dark:text-zinc-400">
                  Conseillé : rattachez votre contenu à un sujet Totemium pour optimiser le RAG.
                </p>
              </div>

              <div className="space-y-2">
                <label className="text-xs font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400" htmlFor="upload-course">
                  Formation WordPress (optionnel)
                </label>
                <select
                  id="upload-course"
                  value={courseId}
                  onChange={(event) => setCourseId(event.target.value)}
                  disabled={uploading || Boolean(subjectId)}
                  className="w-full rounded-xl border border-zinc-200/70 bg-white/90 px-3 py-2 text-sm text-zinc-700 shadow-sm transition focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-200 dark:border-zinc-800 dark:bg-zinc-900/80 dark:text-zinc-200 dark:focus:border-emerald-500 dark:focus:ring-emerald-900/60"
                >
                  <option value="">— choisir —</option>
                  {courses.map((course) => (
                    <option key={course.id} value={course.id}>
                      {course.title}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-zinc-500 dark:text-zinc-400">
                  Si aucun sujet n’est sélectionné, rattachez le média à une formation WordPress.
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-xs font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400" htmlFor="upload-title">
                Titre du média
              </label>
              <input
                id="upload-title"
                type="text"
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                disabled={uploading}
                className="w-full rounded-xl border border-zinc-200/70 bg-white/90 px-3 py-2 text-sm text-zinc-700 shadow-sm transition focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-200 dark:border-zinc-800 dark:bg-zinc-900/80 dark:text-zinc-200 dark:focus:border-emerald-500 dark:focus:ring-emerald-900/60"
                placeholder="Cours IA — Module 1"
                data-onboarding="title-input"
              />
            </div>

            <div
              className={`space-y-3 rounded-2xl border-2 border-dashed p-6 text-center transition ${
                dragOver ? 'border-emerald-500 bg-emerald-50/60 dark:border-emerald-500 dark:bg-emerald-900/20' : 'border-zinc-300 dark:border-zinc-700'
              }`}
              onDragOver={dropHandlers.onDragOver}
              onDragLeave={dropHandlers.onDragLeave}
              onDrop={dropHandlers.onDrop}
              onClick={() => inputRef.current?.click()}
              role="button"
              tabIndex={0}
              data-onboarding="file-zone"
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault()
                  inputRef.current?.click()
                }
              }}
            >
              <input
                ref={inputRef}
                type="file"
                accept="audio/mpeg,video/mp4,video/quicktime,text/plain,.mp3,.mp4,.mov,.wav,.txt"
                onChange={(event) => setFile(event.target.files?.[0] || null)}
                disabled={uploading}
                className="hidden"
              />
              {file ? (
                <div className="space-y-1 text-sm">
                  <p className="font-medium text-zinc-700 dark:text-zinc-100">{file.name}</p>
                  <p className="text-xs text-zinc-500 dark:text-zinc-400">{(file.size / (1024 * 1024)).toFixed(2)} Mo</p>
                </div>
              ) : (
                <div className="space-y-2">
                  <p className="text-sm text-zinc-600 dark:text-zinc-300">Glissez-déposez votre fichier ici, ou cliquez pour parcourir.</p>
                  <p className="text-xs text-zinc-500 dark:text-zinc-400">Formats acceptés : mp3, wav, mp4, mov, txt.</p>
                </div>
              )}
            </div>

            {(uploading || progress > 0) && (
              <div className="space-y-2 text-sm text-zinc-600 dark:text-zinc-300">
                <div className="flex items-center gap-2">
                  <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-emerald-600 border-t-transparent" />
                  {uploading ? `Envoi du fichier… ${progress}%` : `Upload terminé (${progress}%)`}
                </div>
                <div className="h-2 overflow-hidden rounded-full bg-zinc-200 dark:bg-zinc-800">
                  <div className="h-2 bg-emerald-600 transition-all" style={{ width: `${progress}%` }} />
                </div>
              </div>
            )}

            {error && (
              <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900/70 dark:bg-red-950/40 dark:text-red-200">
                {error}
              </div>
            )}

            {success && (
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700 dark:border-emerald-900/70 dark:bg-emerald-950/40 dark:text-emerald-200">
                {success}
              </div>
            )}

            <div className="flex flex-wrap items-center gap-3">
              <button
                type="submit"
                disabled={uploading || !file}
                className="rounded-full border border-emerald-200/70 bg-emerald-500 px-5 py-2 text-sm font-semibold text-white shadow-lg shadow-emerald-500/30 transition hover:bg-emerald-600 disabled:cursor-not-allowed disabled:opacity-60 dark:border-emerald-900/60"
              >
                {uploading ? 'Upload en cours…' : 'Uploader'}
              </button>
              {file && !uploading && (
                <button
                  type="button"
                  onClick={() => {
                    setFile(null)
                    setProgress(0)
                    setSuccess(null)
                  }}
                  className="rounded-full border border-zinc-200/70 px-4 py-2 text-sm font-semibold text-zinc-600 transition hover:bg-zinc-100 dark:border-zinc-700 dark:text-zinc-200 dark:hover:bg-zinc-800"
                >
                  Réinitialiser
                </button>
              )}
            </div>
          </div>
        </form>

        <footer className="border-t border-emerald-100/70 bg-emerald-50/50 px-6 py-4 text-xs text-zinc-500 dark:border-emerald-900/60 dark:bg-emerald-900/20 dark:text-zinc-400">
          Les contenus envoyés s’afficheront dans la bibliothèque après indexation. Surveillez leur statut depuis la page contenus.
        </footer>
      </aside>
    </div>
  )
}
